import { UploadSimpleIcon } from "@phosphor-icons/react";
import { useT } from "@transifex/react";
import { useEffect, useRef, useState } from "react";
import LoadingSpinner from "../../../basic/loadingSpinner/LoadingSpinner";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import { PhosphorIcon } from "../../../basic/phosphorIcon/PhosphorIcon";
import BasePopupWrapper, { TitleColorVariants } from "../../../basic/popups/BasePopupWrapper";
import ColumnSelectionStep, { MAX_SELECTED_COLUMNS } from "./ColumnSelectionStep";
import NameFormatStep from "./NameFormatStep";
import {
    detectStudentColumns,
    extractStudentNames,
    getColumnLetter,
    NameFormats,
    parseStudentTable,
    splitStudentNames,
} from "../../../helpers/StudentListImport";
import { MAX_UPLOADED_STUDENTS } from "../../../constants/LimitConstants";
import { ButtonColorVariant } from "../../teacherArea/AreaButton";

enum UploadStudentListSteps {
    PARSING = "PARSING",
    ERROR = "ERROR",
    TOO_MANY_STUDENTS = "TOO_MANY_STUDENTS",
    COLUMN_SELECTION = "COLUMN_SELECTION",
    NAME_FORMAT = "NAME_FORMAT",
}

interface UploadStudentListPopupProps {
    isOpen: boolean;
    file: File | null;
    /** Dismisses the upload flow and returns to the manual add-students popup. */
    onClose: () => void;
    /** Closes the entire add-students flow. */
    onCloseAll: () => void;
    /** Resolves when the names were handed over; the parent decides what closes afterwards. */
    onSubmit: (names: string[]) => Promise<void>;
    existingClassMemberNames: string[];
}

const UploadStudentListPopup = (props: UploadStudentListPopupProps) => {
    const t = useT();

    const [step, setStep] = useState(UploadStudentListSteps.PARSING);
    const [currentFile, setCurrentFile] = useState<File | null>(null);
    const [rows, setRows] = useState<string[][]>([]);
    const [hasHeader, setHasHeader] = useState(false);
    const [selectedColumnIndices, setSelectedColumnIndices] = useState<number[]>([]);
    const [format, setFormat] = useState(NameFormats.FIRST_NAME_LAST_NAME);
    const [removedNames, setRemovedNames] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (props.isOpen) {
            setCurrentFile(props.file);
        } else {
            setCurrentFile(null);
            setRows([]);
            setRemovedNames([]);
            setIsSubmitting(false);
            setStep(UploadStudentListSteps.PARSING);
        }
    }, [props.isOpen, props.file]);

    useEffect(() => {
        if (!currentFile) return;

        let isCancelled = false;
        setStep(UploadStudentListSteps.PARSING);
        currentFile
            .arrayBuffer()
            .then((data) => {
                if (isCancelled) return;
                const parsedRows = parseStudentTable(data);
                const detected = detectStudentColumns(parsedRows);

                const studentRowCount = detected.hasHeader ? parsedRows.length - 1 : parsedRows.length;
                if (studentRowCount > MAX_UPLOADED_STUDENTS) {
                    setRows([]);
                    setStep(UploadStudentListSteps.TOO_MANY_STUDENTS);
                    return;
                }

                setRows(parsedRows);
                setHasHeader(detected.hasHeader);
                setSelectedColumnIndices(
                    detected.lastNameColumnIndex === null
                        ? [detected.firstNameColumnIndex]
                        : [detected.firstNameColumnIndex, detected.lastNameColumnIndex]
                );
                setFormat(
                    detected.lastNameColumnIndex === null ? NameFormats.FIRST_NAME : NameFormats.FIRST_NAME_LAST_NAME
                );
                setStep(UploadStudentListSteps.COLUMN_SELECTION);
            })
            .catch(() => {
                if (!isCancelled) setStep(UploadStudentListSteps.ERROR);
            });

        return () => {
            isCancelled = true;
        };
    }, [currentFile]);

    const t_title = t("Upload student list", { _tags: "SchoolClasses, UploadStudentListPopup" });
    const t_parsingFile = t("Reading file ...", { _tags: "SchoolClasses, UploadStudentListPopup" });
    const t_parsingError = t("An error occurred while reading the file.", {
        _tags: "SchoolClasses, UploadStudentListPopup",
    });
    const t_uploadDifferentFile = t("Upload a different file", { _tags: "SchoolClasses, UploadStudentListPopup" });
    const t_supportedFormats = t("Please use a CSV or XLSX file.", {
        _tags: "SchoolClasses, UploadStudentListPopup",
    });
    const t_tooManyStudents = t("The file contains too many students.", {
        _tags: "SchoolClasses, UploadStudentListPopup",
    });
    const t_maxStudentsHint = t("A maximum of {maxStudents} students can be uploaded at once.", {
        _tags: "SchoolClasses, UploadStudentListPopup",
        maxStudents: MAX_UPLOADED_STUDENTS,
    });
    const t_column = t("Column", { _tags: "SchoolClasses, UploadStudentListPopup" });
    const t_cancel = t("Cancel", { _tags: "SchoolClasses, UploadStudentListPopup" });
    const t_back = t("Back", { _tags: "SchoolClasses, UploadStudentListPopup" });
    const t_next = t("Next", { _tags: "SchoolClasses, UploadStudentListPopup" });
    const t_close = t("Close", { _tags: "SchoolClasses, UploadStudentListPopup" });
    const t_add = t("Add", { _tags: "SchoolClasses, UploadStudentListPopup" });

    // reduce, not Math.max(...spread): one spread argument per row exceeds the call stack limit
    // for very large files.
    const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
    const dataRows = hasHeader ? rows.slice(1) : rows;

    const getColumnTitle = (columnIndex: number): string => {
        const headerTitle = hasHeader ? rows[0]?.[columnIndex]?.trim() : "";
        return headerTitle ? `${t_column}: ${headerTitle}` : `${t_column} ${getColumnLetter(columnIndex)}`;
    };

    const columnOptions = Array.from({ length: columnCount }, (_, columnIndex) => ({
        name: getColumnTitle(columnIndex),
        value: String(columnIndex),
    }));

    const extractedNames = extractStudentNames(rows, {
        hasHeader,
        firstNameColumnIndex: selectedColumnIndices[0] ?? 0,
        lastNameColumnIndex: selectedColumnIndices.length > 1 ? selectedColumnIndices[1] : null,
        format,
    });
    const splitNames = splitStudentNames(extractedNames, props.existingClassMemberNames);
    const namesToAdd = splitNames.uniqueNames.filter((name) => !removedNames.includes(name));

    const handlePickDifferentFile = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (file) {
            setRemovedNames([]);
            setCurrentFile(file);
        }
    };

    const handleToggleHasHeader = () => {
        const detected = detectStudentColumns(rows);
        if (hasHeader) {
            setHasHeader(false);
        } else {
            setHasHeader(true);
            if (detected.hasHeader) {
                setSelectedColumnIndices(
                    detected.lastNameColumnIndex === null
                        ? [detected.firstNameColumnIndex]
                        : [detected.firstNameColumnIndex, detected.lastNameColumnIndex]
                );
            }
        }
    };

    // Ignores a column that is already used by the other dropdown, so both can never produce
    // names like "Max Max".
    const handleSelectColumn = (position: number, value: string) => {
        const columnIndex = Number(value);
        if (selectedColumnIndices.includes(columnIndex)) return;

        const updated = [...selectedColumnIndices];
        updated[position] = columnIndex;
        setSelectedColumnIndices(updated);
    };

    const handleAddColumn = () => {
        if (selectedColumnIndices.length >= MAX_SELECTED_COLUMNS) return;

        const unusedColumnIndex = Array.from({ length: columnCount }, (_, i) => i).find(
            (i) => !selectedColumnIndices.includes(i)
        );
        if (unusedColumnIndex !== undefined) {
            setSelectedColumnIndices([...selectedColumnIndices, unusedColumnIndex]);
        }
    };

    const handleRemoveColumn = (position: number) => {
        setSelectedColumnIndices(selectedColumnIndices.filter((_, i) => i !== position));
    };

    const handleGoToFormatStep = () => {
        // A single column offers only the full-name option (FIRST_NAME); a leftover two-column
        // format would match no option and leave the dropdown showing nothing.
        if (selectedColumnIndices.length === 1) {
            setFormat(NameFormats.FIRST_NAME);
        }
        setRemovedNames([]);
        setStep(UploadStudentListSteps.NAME_FORMAT);
    };

    const handleChangeFormat = (value: string) => {
        setFormat(value as NameFormats);
        setRemovedNames([]);
    };

    // Guards against a second click landing before the disabled state has rendered.
    const handleSubmit = async () => {
        if (isSubmitting || namesToAdd.length === 0) return;
        setIsSubmitting(true);
        try {
            await props.onSubmit(namesToAdd);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderParsingStep = () => (
        <div className="flex gap-s items-center justify-center py-xl">
            <LoadingSpinner size="h-xl w-xl" />
            <span className="text-l text-p6-grey-dark">{t_parsingFile}</span>
        </div>
    );

    const renderErrorStep = (message: string, hint: string) => (
        <div className="flex flex-col gap-l items-center py-l">
            <div className="flex flex-col gap-xxs items-center">
                <div className="flex gap-s items-center">
                    <PhaseSixIcon
                        name="alert"
                        className="text-xl text-p6-grey-dark shrink-0"
                    />
                    <span className="text-l text-p6-grey-dark">{message}</span>
                </div>
                <span className="text-m text-p6-grey-dark">{hint}</span>
            </div>
            <button
                type="button"
                onClick={handlePickDifferentFile}
                className="flex gap-xs items-center bg-transparent border-none cursor-pointer p-no text-l text-p6-grey-dark hover:text-p6-orange"
            >
                {t_uploadDifferentFile}
                <PhosphorIcon
                    phosphorIcon={UploadSimpleIcon}
                    fontSize="text-xl"
                />
            </button>
        </div>
    );

    const renderStepContent = () => {
        switch (step) {
            case UploadStudentListSteps.PARSING:
                return renderParsingStep();
            case UploadStudentListSteps.ERROR:
                return renderErrorStep(t_parsingError, t_supportedFormats);
            case UploadStudentListSteps.TOO_MANY_STUDENTS:
                return renderErrorStep(t_tooManyStudents, t_maxStudentsHint);
            case UploadStudentListSteps.COLUMN_SELECTION:
                return (
                    <ColumnSelectionStep
                        fileName={currentFile?.name ?? ""}
                        hasHeader={hasHeader}
                        dataRows={dataRows}
                        columnCount={columnCount}
                        columnOptions={columnOptions}
                        selectedColumnIndices={selectedColumnIndices}
                        onToggleHasHeader={handleToggleHasHeader}
                        onSelectColumn={handleSelectColumn}
                        onAddColumn={handleAddColumn}
                        onRemoveColumn={handleRemoveColumn}
                        onPickDifferentFile={handlePickDifferentFile}
                    />
                );
            case UploadStudentListSteps.NAME_FORMAT:
                return (
                    <NameFormatStep
                        format={format}
                        singleColumnTitle={getColumnTitle(selectedColumnIndices[0] ?? 0)}
                        hasMultipleColumns={selectedColumnIndices.length > 1}
                        namesToAdd={namesToAdd}
                        skippedExistingCount={splitNames.skippedExistingNames.length}
                        skippedDuplicateNames={splitNames.skippedDuplicateNames}
                        onChangeFormat={handleChangeFormat}
                        onRemoveName={(name) => setRemovedNames([...removedNames, name])}
                    />
                );
        }
    };

    const stepButtons = {
        [UploadStudentListSteps.PARSING]: {
            secondaryButton: { text: t_cancel, onClick: props.onClose },
            primaryButton: { text: t_next, onClick: () => {}, disabled: true },
        },
        [UploadStudentListSteps.ERROR]: {
            secondaryButton: { text: t_back, onClick: props.onClose },
            primaryButton: { text: t_close, onClick: props.onCloseAll },
        },
        [UploadStudentListSteps.TOO_MANY_STUDENTS]: {
            secondaryButton: { text: t_back, onClick: props.onClose },
            primaryButton: { text: t_close, onClick: props.onCloseAll },
        },
        [UploadStudentListSteps.COLUMN_SELECTION]: {
            secondaryButton: { text: t_back, onClick: props.onClose },
            primaryButton: {
                text: t_next,
                onClick: handleGoToFormatStep,
                disabled: selectedColumnIndices.length === 0,
            },
        },
        [UploadStudentListSteps.NAME_FORMAT]: {
            secondaryButton: {
                text: t_back,
                onClick: () => setStep(UploadStudentListSteps.COLUMN_SELECTION),
                disabled: isSubmitting,
            },
            primaryButton: {
                text: t_add,
                onClick: handleSubmit,
                disabled: namesToAdd.length === 0 || isSubmitting,
            },
        },
    }[step];

    // Clicking outside or pressing escape leaves the whole add-students flow; only the "Back"
    // button steps back to the manual entry popup.
    const handleRequestClose = () => {
        if (isSubmitting) return;
        props.onCloseAll();
    };

    return (
        <BasePopupWrapper
            isOpen={props.isOpen}
            onClose={handleRequestClose}
            title={t_title}
            titleColor={TitleColorVariants.DEFAULT}
            secondaryButton={{ ...stepButtons.secondaryButton, colorVariant: ButtonColorVariant.HIGH_CONTRAST }}
            primaryButton={stepButtons.primaryButton}
        >
            {renderStepContent()}
            <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx"
                onChange={handleFileSelected}
                className="hidden"
            />
        </BasePopupWrapper>
    );
};

export default UploadStudentListPopup;
